import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Calendar, Sparkles, Send, Loader2, Download, ExternalLink, Tag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import {
  usePrograms, programStatus, CATEGORY_LABELS, getSignedUrl, type Program,
} from '@/data/hooks/usePrograms';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { SalesPlaybookCard } from '@/components/programs/SalesPlaybookCard';
import { countdownLabel, urgencyTier } from '@/lib/programStatus';
import { useProgramDealCount } from '@/data/hooks/useDealPrograms';
import { AlertTriangle, Briefcase } from 'lucide-react';

function fmt(iso: string | null) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' });
}

function HighlightList({ title, items }: { title: string; items?: string[] }) {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <div className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{title}</div>
      <ul className="space-y-1 text-sm">
        {items.map((it, i) => <li key={i} className="flex gap-2"><span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-primary" />{it}</li>)}
      </ul>
    </div>
  );
}

export default function ProgramDetail() {
  const { id } = useParams<{ id: string }>();
  const { mode, role } = useAuth();
  const { programs, refresh } = usePrograms();
  const [program, setProgram] = useState<Program | null>(null);
  const [loading, setLoading] = useState(true);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const dealCount = useProgramDealCount(program?.id);
  const canEditPlaybook = role === 'manager' || role === 'admin';

  // Try local cache first, otherwise fetch
  useEffect(() => {
    if (!id) return;
    const local = programs.find(p => p.id === id);
    if (local) { setProgram(local); setLoading(false); return; }
    if (mode === 'demo') { setLoading(false); return; }
    setLoading(true);
    supabase.from('programs').select('*, profiles:uploaded_by(full_name)').eq('id', id).maybeSingle()
      .then(({ data }) => {
        if (data) setProgram({ ...(data as any), uploaded_by_name: (data as any).profiles?.full_name } as Program);
        setLoading(false);
      });
  }, [id, programs, mode]);

  useEffect(() => {
    if (!program || mode === 'demo') return;
    getSignedUrl(program.file_path).then(({ data }) => {
      if (data) setPdfUrl(data.signedUrl);
    });
  }, [program, mode]);

  // AI ask state
  const [question, setQuestion] = useState('');
  const [asking, setAsking] = useState(false);
  const [answer, setAnswer] = useState<string>('');

  const ask = async () => {
    if (!question.trim() || !program) return;
    if (mode === 'demo') {
      setAnswer('Demo mode — sign in to use AI search across your real uploaded PDFs.');
      return;
    }
    setAsking(true); setAnswer('');
    try {
      const { data, error } = await supabase.functions.invoke('ask-program', {
        body: { question, program_id: program.id },
      });
      if (error) throw error;
      setAnswer((data as any)?.answer ?? 'No answer.');
    } catch (e: any) {
      toast.error(e.message ?? 'Could not get answer');
    } finally {
      setAsking(false);
    }
  };

  const status = useMemo(() => program ? programStatus(program) : null, [program]);

  if (loading) return <div className="p-8 text-center text-muted-foreground">Loading…</div>;
  if (!program) return (
    <div className="p-8 text-center">
      <p className="text-muted-foreground">Program not found.</p>
      <Button asChild variant="link" className="mt-2"><Link to="/programs">Back to Programs Hub</Link></Button>
    </div>
  );

  return (
    <div className="space-y-5">
      <div>
        <Link to="/programs" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" />Programs Hub
        </Link>
      </div>

      {(() => {
        const tier = urgencyTier(program);
        if (tier === 'expired') {
          return (
            <div className="rounded-xl border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-300 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" /> This program has expired ({countdownLabel(program)}).
            </div>
          );
        }
        if (tier === 'final' || tier === 'soon') {
          const isFinal = tier === 'final';
          return (
            <div className={`rounded-xl border p-3 text-sm flex items-center gap-2 ${
              isFinal ? 'border-red-500/40 bg-red-500/10 text-red-300'
              : 'border-yellow-500/40 bg-yellow-500/10 text-yellow-300'
            }`}>
              <AlertTriangle className="h-4 w-4" />
              <span className="font-semibold">{countdownLabel(program)}</span>
              {program.expiry_date && (
                <span className="text-muted-foreground">· {fmt(program.expiry_date)}</span>
              )}
            </div>
          );
        }
        return null;
      })()}

      <div className="rounded-2xl border border-border/60 bg-card/80 p-6 backdrop-blur-sm">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-muted-foreground">{CATEGORY_LABELS[program.category]}</span>
              {status === 'expired' && <span className="rounded bg-red-500/15 px-2 py-0.5 text-[10px] font-bold uppercase text-red-400">Expired</span>}
              {status === 'expiring_soon' && <span className="rounded bg-yellow-500/15 px-2 py-0.5 text-[10px] font-bold uppercase text-yellow-400">Expiring soon</span>}
              {status === 'active' && <span className="rounded bg-green-500/15 px-2 py-0.5 text-[10px] font-bold uppercase text-green-400">Active</span>}
            </div>
            <h1 className="mt-1 text-2xl font-bold">{program.title}</h1>
            {program.summary && <p className="mt-2 max-w-3xl text-sm text-muted-foreground">{program.summary}</p>}
          </div>
          <div className="flex gap-2">
            {pdfUrl && (
              <>
                <Button asChild variant="outline" className="gap-2"><a href={pdfUrl} target="_blank" rel="noopener"><ExternalLink className="h-4 w-4" />Open</a></Button>
                <Button asChild className="gap-2"><a href={pdfUrl} download><Download className="h-4 w-4" />Download</a></Button>
              </>
            )}
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" />Effective {fmt(program.effective_date)}</span>
          <span className="inline-flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" />Expires {fmt(program.expiry_date)}</span>
          <span>Uploaded by {program.uploaded_by_name ?? 'Manager'} · {fmt(program.created_at)}</span>
        </div>
        {program.keywords?.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {program.keywords.map(k => (
              <span key={k} className="inline-flex items-center gap-1 rounded-full bg-muted/40 px-2 py-0.5 text-[11px] text-muted-foreground">
                <Tag className="h-3 w-3" />{k}
              </span>
            ))}
          </div>
        )}
        {dealCount > 0 && (
          <div className="mt-4 inline-flex items-center gap-2 rounded-lg border border-border/40 bg-muted/20 px-3 py-1.5 text-xs">
            <Briefcase className="h-3.5 w-3.5 text-primary" />
            <span className="font-semibold">{dealCount}</span>
            <span className="text-muted-foreground">{dealCount === 1 ? 'deal' : 'deals'} using this program</span>
          </div>
        )}
      </div>

      <SalesPlaybookCard
        program={program}
        canEdit={canEditPlaybook}
        isDemo={mode === 'demo'}
        onSaved={() => { refresh(); }}
      />

      <div className="grid gap-5 lg:grid-cols-5">
        {/* AI panel */}
        <Card className="border-border/60 bg-card/80 p-5 lg:col-span-2">
          <div className="mb-3 flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            <h2 className="text-sm font-semibold uppercase tracking-wider">AI Summary</h2>
          </div>
          <div className="space-y-4">
            <HighlightList title="Key highlights" items={program.ai_highlights?.key_points} />
            <HighlightList title="Rates" items={program.ai_highlights?.rates} />
            <HighlightList title="Rebates" items={program.ai_highlights?.rebates} />
            <HighlightList title="Deadlines" items={program.ai_highlights?.deadlines} />
            <HighlightList title="What changed" items={program.ai_highlights?.what_changed} />
            {!program.ai_highlights || Object.keys(program.ai_highlights).length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {program.processing_status === 'pending' ? 'AI summary still processing…' : 'No AI highlights available.'}
              </p>
            ) : null}
          </div>

          <div className="mt-6 border-t border-border/40 pt-4">
            <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Ask this program</div>
            <div className="flex gap-2">
              <Input
                value={question}
                onChange={e => setQuestion(e.target.value)}
                placeholder='e.g. "What incentives expire this month?"'
                onKeyDown={e => { if (e.key === 'Enter') ask(); }}
              />
              <Button onClick={ask} disabled={asking || !question.trim()} size="icon">
                {asking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </div>
            {answer && (
              <div className="mt-3 rounded-lg border border-border/40 bg-muted/20 p-3 text-sm whitespace-pre-wrap">
                {answer}
              </div>
            )}
          </div>
        </Card>

        {/* PDF preview */}
        <Card className="border-border/60 bg-card/80 p-3 lg:col-span-3">
          {mode === 'demo' ? (
            <div className="flex h-[700px] items-center justify-center text-center text-sm text-muted-foreground">
              Demo mode — PDF preview is disabled. The AI summary on the left is fully populated.
            </div>
          ) : pdfUrl ? (
            <iframe src={pdfUrl} className="h-[700px] w-full rounded" title={program.title} />
          ) : (
            <div className="flex h-[700px] items-center justify-center text-sm text-muted-foreground">Loading PDF…</div>
          )}
        </Card>
      </div>
    </div>
  );
}